import { atom } from "jotai";

/**
 * Measurement tool state atoms
 *
 * These atoms manage measurement tool state for the viewer.
 * Annotations are session-only (not persisted).
 */

// =============================================================================
// Tool Mode State
// =============================================================================

// Available tool modes for primary mouse button
// 'regionGrow' is a V2 tool (S3) — gated by segmentation assist config
export type MeasurementToolMode =
  | "none"
  | "pan"
  | "zoom"
  | "length"
  | "rectangleROI"
  | "lasso"
  | "bump"
  | "regionGrow";

// Current active tool (none = default WindowLevel mode)
export const annotationSaveCountAtom = atom(0);
const activeToolStateAtom = atom<MeasurementToolMode>("none");
export const activeMeasurementToolAtom = atom(
  (get) => get(activeToolStateAtom),
  (
    get,
    set,
    update: MeasurementToolMode | ((current: MeasurementToolMode) => MeasurementToolMode),
  ) => {
    const tool = typeof update === "function" ? update(get(activeToolStateAtom)) : update;
    if (get(annotationSaveCountAtom) > 0 && ["lasso", "bump", "regionGrow"].includes(tool)) return;
    set(activeToolStateAtom, tool);
  },
);

// Stores the drawing tool (lasso/bump/regionGrow) that was active before
// switching to a measurement tool (ruler/rectangleROI). Allows toggling
// back to the drawing tool after measuring.
export const previousDrawingToolAtom = atom<MeasurementToolMode | null>(null);

// Mirror of Cornerstone3D's annotation-selection state. Populated by
// useAnnotationSelectionMirror, which subscribes to ANNOTATION_SELECTION_CHANGE
// and writes the current selected UIDs here so React (and the
// deleteSelectedAnnotation command) can read selection without polling.
export const selectedAnnotationsAtom = atom<string[]>([]);

// =============================================================================
// Help Dialog State
// =============================================================================

// Help dialog visibility
export const helpDialogOpenAtom = atom<boolean>(false);

// =============================================================================
// Diagnostic Dialog State
// =============================================================================

// Viewer-diagnostic dialog visibility (GPU verdict, renderer, cache).
export const diagnosticDialogOpenAtom = atom<boolean>(false);

// =============================================================================
// Clear-all-measurements confirmation
// =============================================================================

// Confirm-dialog visibility for the destructive "clear all measurements" action.
// Atom-driven (mirrors helpDialogOpenAtom) so the toolbar button, the keyboard
// shortcut (Ctrl/Cmd+Shift+Backspace), and the right-click menu item all open the
// SAME confirm without each owning a copy of the dialog. The dialog itself is
// rendered once in App.tsx and runs the clearAllMeasurements command on confirm.
export const clearAllMeasurementsConfirmAtom = atom<boolean>(false);
